/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import StatusPill from './StatusPill';
import CreateShiftForm from './CreateShiftForm';
import FlaggedOnSite from './FlaggedOnSite';
import DeploymentUserManagement from './DeploymentUserManagement';

const SUPER_ADMIN_UPN = "oliver.watts@onwave.com";

/**
 * ShiftsIndex — desktop deployment hub with tabs: Shifts, Flagged, User Management.
 */
const SHIFTS_GRID = '1.2fr 2fr 0.7fr 1.2fr 1.2fr 1fr 1.1fr 0.8fr 0.5fr';

export default function ShiftsIndex({ st, setSt, account, audit, isAdmin, isManager, onSelectShift, initialTab, onTabChange }) {
  const [tab, setTabLocal] = useState(initialTab || 'shifts');
  const setTab = (t) => { setTabLocal(t); if (onTabChange) onTabChange(t); };
  useEffect(() => { if (initialTab && initialTab !== tab) setTabLocal(initialTab); }, [initialTab]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  const shifts = st.shifts || [];
  const allocs = st.allocations || [];
  const locs = (st.locations || []).filter(l => !['In Stock', 'In Testing', 'Quarantine'].includes(l.name));

  const effectiveStatus = (s) => {
    if (s.status === 'planned' && new Date(s.startAt) <= new Date()) return 'active';
    return s.status;
  };

  const filtered = useMemo(() => {
    let result = [...shifts];
    if (statusFilter !== 'all') result = result.filter(s => effectiveStatus(s) === statusFilter);
    if (locationFilter !== 'all') result = result.filter(s => s.locationId === locationFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        s.title?.toLowerCase().includes(q) ||
        s.possessionRef?.toLowerCase().includes(q) ||
        s.site?.toLowerCase().includes(q) ||
        (locs.find(l => l.id === s.locationId)?.name || '').toLowerCase().includes(q)
      );
    }
    return result.sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
  }, [shifts, statusFilter, locationFilter, search, locs]);

  // Group filtered shifts by possessionRef + locationId + week. Single-member groups render flat.
  const grouped = useMemo(() => {
    const groups = new Map();
    filtered.forEach(s => {
      const key = (s.possessionRef || '') + '|' + (s.locationId || '') + '|' + (s.week ?? '');
      if (!groups.has(key)) groups.set(key, { key, shifts: [] });
      groups.get(key).shifts.push(s);
    });
    // Sort each group by shiftNumber (then startAt as tiebreak), then sort groups by their earliest startAt desc
    const arr = Array.from(groups.values()).map(g => ({
      ...g,
      shifts: [...g.shifts].sort((a, b) => (a.shiftNumber - b.shiftNumber) || (new Date(a.startAt) - new Date(b.startAt))),
    }));
    arr.sort((a, b) => new Date(b.shifts[0].startAt) - new Date(a.shifts[0].startAt));
    return arr;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: shifts.length, active: 0, planned: 0, closed: 0 };
    shifts.forEach(s => { const es = effectiveStatus(s); c[es] = (c[es] || 0) + 1; });
    c.closed = (c.closed || 0) + (c.closed_with_open_items || 0);
    return c;
  }, [shifts]);

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const fmtTime = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const flaggedCount = useMemo(() => (st.allocations || []).filter(a => a.status === 'flagged_missing' && !a.recoveredAt).length, [st.allocations]);

  if (showCreate) {
    return <CreateShiftForm st={st} setSt={setSt} account={account} audit={audit} onBack={() => setShowCreate(false)} />;
  }

  // Tab strip style helper
  const tabStyle = (id) => ({
    padding: '10px 18px', background: 'transparent', border: 'none',
    borderBottom: tab === id ? '2px solid var(--brand-default)' : '2px solid transparent',
    color: tab === id ? 'var(--text-default)' : 'var(--text-muted)',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 6,
  });

  return (
    <div>
      {/* Tab strip */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-default)', marginBottom: 22 }}>
        <button style={tabStyle('shifts')} onClick={() => setTab('shifts')}>Shifts</button>
        <button style={tabStyle('flagged')} onClick={() => setTab('flagged')}>
          Flagged on site
          {flaggedCount > 0 && <span className="pill pill-danger" style={{ fontSize: 9, padding: '1px 6px' }}>{flaggedCount}</span>}
        </button>
        {(isAdmin || isManager) && (
          <button style={tabStyle('users')} onClick={() => setTab('users')}>User Management</button>
        )}
        {tab === 'shifts' && (isAdmin || isManager) && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingBottom: 6 }}>
            <button onClick={() => setShowCreate(true)} className="btn btn-primary">+ Create shift</button>
          </div>
        )}
      </div>

      {/* Flagged tab */}
      {tab === 'flagged' && (
        <FlaggedOnSite st={st} setSt={setSt} account={account} audit={audit} isAdmin={isAdmin} isManager={isManager} />
      )}

      {/* User management tab */}
      {tab === 'users' && (isAdmin || isManager) && (
        <DeploymentUserManagement st={st} setSt={setSt} account={account} audit={audit} isAdmin={isAdmin} SUPER_ADMIN_UPN={SUPER_ADMIN_UPN} />
      )}

      {/* Shifts tab */}
      {tab === 'shifts' && (<>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-default)', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>Shifts</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Manage possession shifts and device allocations across all locations.</div>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
        {[
          { label: 'Total shifts', value: counts.all, color: 'var(--brand-default)' },
          { label: 'Active', value: counts.active, color: '#4ade80' },
          { label: 'Planned', value: counts.planned, color: '#60a5fa' },
          { label: 'Closed', value: counts.closed, color: 'var(--text-muted)' },
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: '14px 16px', cursor: 'default' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="input-with-icon" style={{ width: 220 }}>
          <span className="input-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search shifts..." style={{ width: '100%' }} />
          {search && <button onClick={() => setSearch('')} aria-label="Clear search" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>}
        </div>
        {['all', 'active', 'planned', 'closed'].map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className="btn btn-ghost"
            style={{
              textTransform: 'capitalize',
              background: statusFilter === f ? 'var(--brand-tint)' : undefined,
              borderColor: statusFilter === f ? 'var(--brand-border)' : undefined,
              color: statusFilter === f ? 'var(--brand-default)' : undefined,
            }}
          >
            {f} · {counts[f] || 0}
          </button>
        ))}
        <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)} className="input" style={{ marginLeft: 'auto', width: 'auto', padding: '6px 28px 6px 10px', fontSize: 12 }}>
          <option value="all">All locations</option>
          {locs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {/* Shifts grid table */}
      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>No shifts found</div>
          <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
            {shifts.length === 0 ? 'Create your first shift to get started.' : 'Try adjusting your filters.'}
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 6, paddingLeft: 2 }}>
            {filtered.length} shift{filtered.length !== 1 ? 's' : ''}{grouped.length !== filtered.length ? ` in ${grouped.length} group${grouped.length !== 1 ? 's' : ''}` : ''}
          </div>
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: SHIFTS_GRID, gap: 12, padding: '10px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
              {['Location', 'Possession / Title', 'Shift / Week', 'Date', 'Window', 'Supervisor', 'Devices', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: 0.6, textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {/* Rows */}
            {grouped.map(group => {
              const isMulti = group.shifts.length > 1;
              const isExpanded = !!expandedGroups[group.key];
              const first = group.shifts[0];
              const last = group.shifts[group.shifts.length - 1];
              const loc = locs.find(l => l.id === first.locationId);

              const renderShiftRow = (shift, indented) => {
                const sLoc = locs.find(l => l.id === shift.locationId);
                const shiftAllocs = allocs.filter(a => a.shiftId === shift.id);
                const outCount = shiftAllocs.filter(a => a.status === 'out').length;
                const totalCount = shiftAllocs.length;
                const status = effectiveStatus(shift);
                return (
                  <div
                    key={shift.id}
                    onClick={() => onSelectShift(shift.id)}
                    style={{
                      display: 'grid', gridTemplateColumns: SHIFTS_GRID, alignItems: 'center', gap: 12,
                      padding: '12px 16px', paddingLeft: indented ? 38 : 16,
                      background: indented ? 'var(--surface-inset)' : 'var(--surface-default)',
                      borderBottom: '1px solid var(--border-default)',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = indented ? 'var(--surface-raised)' : 'var(--surface-inset)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = indented ? 'var(--surface-inset)' : 'var(--surface-default)'; }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{indented ? '' : (sLoc?.name || '—')}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shift.possessionRef}{shift.anonymousMode && <span style={{ marginLeft: 6, padding: '1px 5px', background: 'rgba(191,128,255,0.15)', color: '#bf80ff', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' }}>ANON</span>}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shift.title}</div>
                    </div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{shift.shiftNumber} / {shift.week}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtDate(shift.startAt)}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtTime(shift.startAt)} → {fmtTime(shift.endAt)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{shift.supervisorUserId || '—'}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: totalCount > 0 ? 'var(--text-primary)' : 'var(--text-ghost)' }}>
                      {totalCount > 0 ? `${outCount} out / ${totalCount} total` : '—'}
                    </div>
                    <div><StatusPill status={status} /></div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={(e) => { e.stopPropagation(); onSelectShift(shift.id); }} className="btn btn-ghost btn-sm">View</button>
                    </div>
                  </div>
                );
              };

              if (!isMulti) return renderShiftRow(first, false);

              // Group parent row
              const groupAllocs = allocs.filter(a => group.shifts.some(s => s.id === a.shiftId));
              const groupOut = groupAllocs.filter(a => a.status === 'out').length;
              const groupTotal = groupAllocs.length;
              const statuses = new Set(group.shifts.map(effectiveStatus));
              const groupStatus = statuses.size === 1 ? group.shifts[0] && effectiveStatus(group.shifts[0]) : 'mixed';
              const sameDate = fmtDate(first.startAt) === fmtDate(last.endAt);

              return (
                <React.Fragment key={group.key}>
                  <div
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                    style={{
                      display: 'grid', gridTemplateColumns: SHIFTS_GRID, alignItems: 'center', gap: 12,
                      padding: '12px 16px', background: 'var(--surface-default)', borderBottom: '1px solid var(--border-default)',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-inset)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-default)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0, color: 'var(--text-subtle)' }}><polyline points="9 18 15 12 9 6"/></svg>
                      {loc?.name || '—'}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {first.possessionRef}
                        <span style={{ padding: '1px 6px', background: 'var(--brand-tint)', color: 'var(--brand-default)', border: '1px solid var(--brand-border)', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' }}>{group.shifts.length} SHIFTS</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{first.title}</div>
                    </div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{first.shiftNumber}–{last.shiftNumber} / {first.week}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{sameDate ? fmtDate(first.startAt) : `${fmtDate(first.startAt)} → ${fmtDate(last.endAt)}`}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtTime(first.startAt)} → {fmtTime(last.endAt)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{first.supervisorUserId || '—'}</div>
                    <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: groupTotal > 0 ? 'var(--text-primary)' : 'var(--text-ghost)' }}>
                      {groupTotal > 0 ? `${groupOut} out / ${groupTotal} total` : '—'}
                    </div>
                    <div>{groupStatus === 'mixed' ? <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: 'var(--surface-inset)', color: 'var(--text-muted)', fontWeight: 600 }}>Mixed</span> : <StatusPill status={groupStatus} />}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 11, color: 'var(--text-subtle)' }}>{isExpanded ? 'Hide' : 'Expand'}</div>
                  </div>
                  {isExpanded && group.shifts.map(s => renderShiftRow(s, true))}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
      </>)}
    </div>
  );
}
